import { Preferences } from '@capacitor/preferences';
import { NavigationGuardNext, RouteLocationNormalized } from 'vue-router';
import { apiService } from './api';

// 🔒 Storage: Capacitor Preferences (mobile) + localStorage (web dev)
// 📌 Pour la production, remplacez par @capacitor-secure-storage-plugin
const secureStorage = {
  async set(key: string, value: string) {
    if ((window as any).Capacitor?.isNative) {
      await Preferences.set({ key, value });
    } else {
      localStorage.setItem(key, value);
    }
  },
  async get(key: string): Promise<string | null> {
    if ((window as any).Capacitor?.isNative) {
      const { value } = await Preferences.get({ key });
      return value;
    }
    return localStorage.getItem(key);
  },
  async remove(key: string) {
    if ((window as any).Capacitor?.isNative) {
      await Preferences.remove({ key });
    } else {
      localStorage.removeItem(key);
    }
  }
};

export interface User {
  id: string;
  email: string;
  language?: string;
  country?: string;
}

// ✅ 1. Fonctions d'authentification
export const authService = {
  async signUp(email: string, password: string, meta: Record<string, any> = {}) {
    const res = await apiService.auth.register(email, password, meta);
    await secureStorage.set('receiptai_token', res.session.access_token);
    if (res.session.refresh_token) await secureStorage.set('receiptai_refresh_token', res.session.refresh_token);
    await secureStorage.set('receiptai_user', JSON.stringify(res.user));
    return res;
  },

  async signIn(email: string, password: string) {
    const res = await apiService.auth.login(email, password);
    await secureStorage.set('receiptai_token', res.session.access_token);
    if (res.session.refresh_token) await secureStorage.set('receiptai_refresh_token', res.session.refresh_token);
    await secureStorage.set('receiptai_user', JSON.stringify(res.user));

    // Appliquer un code de parrainage en attente (saisi à l'onboarding)
    const pendingReferral = localStorage.getItem('recash_pending_referral');
    if (pendingReferral) {
      apiService.referral.applyCode(pendingReferral).catch(() => {});
      localStorage.removeItem('recash_pending_referral');
    }

    return res;
  },

  async signOut() {
    try { await apiService.auth.logout(); } catch {}
    await secureStorage.remove('receiptai_token');
    await secureStorage.remove('receiptai_refresh_token');
    await secureStorage.remove('receiptai_user');
    window.location.href = '/onboarding';
  },

  async getCurrentUser(): Promise<User | null> {
    const userStr = await secureStorage.get('receiptai_user');
    if (!userStr) return null;
    try {
      return JSON.parse(userStr) as User;
    } catch {
      await this.signOut();
      return null;
    }
  },

  async isAuthenticated(): Promise<boolean> {
    const token = await secureStorage.get('receiptai_token');
    return !!token;
  }
};

// ✅ 3. Vue Router Guard (compatible Vue 4 / Ionic)
export const authGuard = async (
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth);
  const isGuest = localStorage.getItem('receiptai_guest') === 'true';

  // 🔒 Protection des routes authentifiées
  if (requiresAuth && !isGuest) {
    const isAuth = await authService.isAuthenticated();
    if (!isAuth) {
      return next('/onboarding');
    }
  }

  // 🔄 Redirection automatique si déjà connecté
  if (to.path === '/onboarding') {
    const isAuth = await authService.isAuthenticated();
    if (isAuth) return next('/tabs/home');
  }

  next();
};